import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { Box, Button, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material"
import { cloneElement, useState } from "react"
import noop from "lib/noop"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Bound } from "lib/@components/binding/Bound"

export function BoundStandardStepper({
    children,
    continueCaption = "Continue",
    finishCaption = "Finish",
    backCaption = "Back",
    target,
    step,
    ...props
}) {
    const { target: outerTarget } = useBoundContext()
    const [currentStep, setStep] = useBoundValue("step")
    const [ok, setOk] = useBoundValue("ok", {})
    const [next, setNext] = useState(noop)
    const [cache] = useState({ canContinue: {}, setLabel: {}, setOptional: {}, setContinueFunction: {} })
    const [labels, setLabel] = useBoundValue("labels", {})
    const [optionals, setOptional] = useBoundValue("optional", {})
    children = Array.isArray(children) ? children : [children]
    step ??= currentStep
    target ??= outerTarget
    const Content = props.orientation === "vertical" ? StepContent : Box
    return (
        <Bound target={target}>
            <Stepper step={step} {...props}>
                {children.map(convertStep)}
            </Stepper>
        </Bound>
    )

    function convertStep(child, index) {
        return (
            <Step key={child.key || index} data-cy={`step-${index}`}>
                <StepLabel
                    data-cy={`stepper-label-${index}`}
                    sx={{ height: 55 }}
                    optional={
                        <Typography variant="caption" color="text.secondary">
                            {optionals[index] || child.props.optional}
                        </Typography>
                    }
                >
                    {labels[index] || child.props.label}
                </StepLabel>
                <Content>
                    <Stack spacing={1}>
                        <Box>
                            {cloneElement(child, {
                                canContinue: (cache.canContinue[index] ??= (can) =>
                                    setOk((ok) => ({
                                        ...ok,
                                        [index]: can,
                                    }))),
                                setLabel: (cache.setLabel[index] ??= (label) =>
                                    setLabel((labels) => ({
                                        ...labels,
                                        [index]: label,
                                    }))),
                                setOptional: (cache.setOptional[index] ??= (optional) =>
                                    setOptional((optionals) => ({
                                        ...optionals,
                                        [index]: optional,
                                    }))),
                                setContinueFunction: (cache.setContinueFunction[index] ??= (fn) => setNext(() => fn)),
                                active: step === index,
                            })}
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            {!children[index].props.blockBack && (
                                <Button
                                    data-cy={`stepper-back-btn-${index}`}
                                    disabled={index === 0}
                                    onClick={() => setStep(index - 1)}
                                    sx={{ mt: 1, mr: 1 }}
                                    startIcon={<Iconify icon="ic:round-keyboard-backspace" />}
                                >
                                    {backCaption}
                                </Button>
                            )}
                            {index !== children.length - 1 && (
                                <Button
                                    data-cy={`stepper-continue-btn-${index}`}
                                    disabled={!ok[index]}
                                    variant="contained"
                                    onClick={nextStep(index)}
                                    sx={{ mt: 1, mr: 1 }}
                                >
                                    {index === children.length - 1
                                        ? finishCaption
                                        : children[index].props.continueCaption ?? continueCaption}
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </Content>
            </Step>
        )
    }

    function nextStep(index) {
        return () => {
            next()
            setNext(noop)
            setStep(index + 1)
        }
    }
}
